// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { AppIcon, Badge, IconButton } from '@loa/ui-components';
import {
  intelligenceApi,
  streamMessage,
  type ChatAttachment,
  type Citation,
  type CreateSessionResponse,
} from '../../api/intelligenceApi';
import { VegaChart } from './VegaChart';
import { MermaidDiagram } from './MermaidDiagram';
import { useVoiceChat } from '../../hooks/useVoiceChat';
import { useAdvisorPageContext } from '../../contexts/PageContextProvider';
import { ConfirmationCard } from '../companion/ConfirmationCard';
import { companionPlaceholder } from '../companion/companionPlaceholder';
import { relationshipCashflowApi } from '../../api/relationshipCashflowApi';
import 'katex/dist/katex.min.css';
import './FundChatbot.css';

// Phase 1a (PRD risk R-3): cap Mermaid source length so the LLM can't
// emit a graph with thousands of nodes that locks the browser. Vega
// already polices itself via the existing sanitiser; KaTeX expressions
// are short by nature.
const MAX_MERMAID_SOURCE_BYTES = 4096;

/**
 * Browser-side mirror of intelligence-service's
 * `intelligence.attachment.*` caps. The server is authoritative; these
 * exist so the user gets an inline rejection rather than a 413 after a
 * slow upload.
 */
const ATTACHMENT_LIMITS = {
  maxFileBytes: 10 * 1024 * 1024,        // 10 MB per file
  maxTotalBytes: 32 * 1024 * 1024,       // 32 MB total per message
  maxFilesPerMessage: 5,
  acceptedMimeTypes: [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
  ],
} as const;

const FILE_INPUT_ACCEPT = ATTACHMENT_LIMITS.acceptedMimeTypes.join(',');

interface PendingAttachment {
  id: string;
  name: string;
  mediaType: string;
  bytes: number;
  /** Base64-encoded bytes (no data: URL prefix). */
  base64: string;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));
        return;
      }
      // data:application/pdf;base64,XXXX — strip the prefix
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

function humanFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Fund Center chatbot.
 *
 * Floating launcher button bottom-right of the Funds page. Click opens
 * a 400px right-side dock with a streaming chat to the
 * intelligence-service FUND_RESEARCH use case. The dock is opt-in (no
 * auto-open) and dismissable; the session persists across open/close
 * for the lifetime of the page mount.
 *
 * Tool calls surface visually as inline indicators (🔍 Searching, 📊
 * Comparing, 📄 Reading factsheet). The assistant's streamed tokens
 * accumulate into the last message bubble in real time.
 *
 * Fund references in responses can be auto-linked by the assistant
 * with a `fund:` URI scheme (e.g. `[Aviva Bond Fund Net](fund:aviva:60)`).
 * Clicks on those scroll the funds list to the matching row via the
 * supplied `onSelectFund` callback. Other URLs open in a new tab.
 */
export interface FundChatbotProps {
  /** Called when the user clicks a `fund:<key>` link in an assistant reply. */
  onSelectFund?: (fundKey: string) => void;
  /**
   * Use case to create the session against. Defaults to
   * `ADVISOR_COMPANION` (the global, page-aware companion). The legacy
   * Fund-Center-only experience still resolves via the carry-over of the
   * FUND_RESEARCH tools — pass `'FUND_RESEARCH'` here only for a
   * deliberate rollback (the row + handler will be removed once
   * telemetry confirms zero new FUND_RESEARCH sessions).
   */
  useCaseCode?: 'ADVISOR_COMPANION' | 'FUND_RESEARCH';
}

type ChatRole = 'user' | 'assistant';

interface UiMessage {
  id: string;
  role: ChatRole;
  text: string;
  /** True while tokens are still streaming into this assistant bubble. */
  streaming?: boolean;
  /** Tool names invoked while producing this assistant turn (drives the
   *  persistent "agent" attribution badges below). */
  tools?: string[];
  /** Clickable sources surfaced by the Tax Agent tools this turn. */
  citations?: Citation[];
}

type BadgeColor = 'grey' | 'orange' | 'yellow' | 'green' | 'red' | 'blue' | 'teal' | 'neutral' | 'purple';
interface AgentMeta {
  key: string;
  label: string;
  emoji: string;
  color: BadgeColor;
}

// The specialist "agents" surfaced as attribution badges on an answer. ONLY
// Tax Agent and Fund Center are attributable — they're distinct capabilities
// worth calling out. Reading the relationship record (client / profile / policy
// / quotes / LOAs / CoAs / alerts / provider / NOTES) is core Fin behaviour, not
// a separate agent, so those tools are intentionally NOT mapped and produce no
// badge. Keep in sync with the tool registry (intelligence-service tool/tools).
const AGENTS: Record<string, AgentMeta> = {
  tax: { key: 'tax', label: 'Tax Agent', emoji: '🏛️', color: 'purple' },
  advice: { key: 'advice', label: 'Advanced Financial Advice', emoji: '⚖️', color: 'blue' },
  funds: { key: 'funds', label: 'Fund Center', emoji: '📈', color: 'teal' },
};

const TOOL_AGENT: Record<string, string> = {
  // Tax Agent (cites sources)
  search_reference_library: 'tax',
  search_reference_knowledge: 'tax',
  // Advanced Financial Advice — regulatory / conduct corpus (cites sources)
  search_advice_library: 'advice',
  // Fund Center
  search_funds: 'funds',
  get_fund: 'funds',
  get_fund_factsheet: 'funds',
  compare_funds: 'funds',
  list_favourites: 'funds',
  list_strategies: 'funds',
  // Relationship + Notes (client / profile / policy / quotes / LOAs / CoAs /
  // alerts / provider / notes) are deliberately omitted — not attributed.
};

// Transient "while running" indicator shown under the thread during a tool call.
const TOOL_LABEL: Record<string, string> = {
  search_funds: '🔍 Searching the catalogue…',
  get_fund: '📋 Looking up fund details…',
  list_favourites: '⭐ Reading your favourites…',
  list_strategies: '📚 Reading strategies…',
  compare_funds: '📊 Comparing funds…',
  get_fund_factsheet: '📄 Reading the factsheet…',
  search_reference_knowledge: '🏛️ Consulting the Tax Agent…',
  search_reference_library: '🏛️ Consulting the Tax Agent…',
  search_advice_library: '⚖️ Checking advice & compliance references…',
};

/** The live indicator text for a running tool — a specific label, else the
 *  tool's agent, else a neutral fallback (never a raw tool name). */
function toolIndicator(toolName: string): string {
  if (TOOL_LABEL[toolName]) return TOOL_LABEL[toolName];
  const agent = AGENTS[TOOL_AGENT[toolName]];
  return agent ? `${agent.emoji} Consulting the ${agent.label}…` : 'Working…';
}

/** Distinct agents whose tools were used in an assistant turn (for badges). */
function agentBadges(tools?: string[]): AgentMeta[] {
  if (!tools || tools.length === 0) return [];
  const keys = new Set<string>();
  const out: AgentMeta[] = [];
  for (const t of tools) {
    const agent = AGENTS[TOOL_AGENT[t]];
    if (agent && !keys.has(agent.key)) {
      keys.add(agent.key);
      out.push(agent);
    }
  }
  return out;
}

export default function FundChatbot({ onSelectFund, useCaseCode = 'ADVISOR_COMPANION' }: FundChatbotProps) {
  // Current page context — included in every message so Fin can resolve
  // "this client" / "this relationship" deictics without the advisor
  // naming the entity. Subscribers re-render on navigation; we read
  // through a ref in the send-turn callback so the captured context is
  // always the latest.
  const pageContext = useAdvisorPageContext();
  const pageContextRef = useRef(pageContext);
  useEffect(() => {
    pageContextRef.current = pageContext;
  }, [pageContext]);

  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<CreateSessionResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [sending, setSending] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<(() => void) | null>(null);

  // Voice IO. `enabled` flows from `session.voiceEnabled` (which carries
  // `chat_use_cases.voice_enabled` merged with the brokerage override) —
  // when false, the hook reports unsupported and the mic button is hidden.
  // All audio IO is browser-native; no audio data leaves the browser.
  const voice = useVoiceChat({
    enabled: session?.voiceEnabled === true,
    profile: session?.voiceProfile,
    onTranscript: (text) => {
      setInput((prev) => (prev ? `${prev} ${text}` : text));
    },
  });
  // Stable ref so the message-stream side-effect can call into the speak
  // helper without re-subscribing every render.
  const voiceSpeakRef = useRef(voice.speak);
  useEffect(() => { voiceSpeakRef.current = voice.speak; }, [voice.speak]);

  // Auto-scroll to the bottom whenever messages or the tool indicator change.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, activeTool]);

  // Tear down any in-flight stream + void the session on unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.();
      if (session?.sessionId) {
        // Best-effort — the BFF expires sessions after 4h anyway.
        intelligenceApi.voidSession(session.sessionId).catch(() => {
          /* ignore */
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureSession = useCallback(async (): Promise<CreateSessionResponse | null> => {
    if (session) return session;
    setCreating(true);
    setError(null);
    try {
      const s = await intelligenceApi.createSession(useCaseCode);
      setSession(s);
      return s;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to start chat session';
      setError(msg);
      return null;
    } finally {
      setCreating(false);
    }
  }, [session, useCaseCode]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    await ensureSession();
  }, [ensureSession]);

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const handleClose = useCallback(() => {
    // PRD R-5 mitigation (OQ-3 consequence): if the session has messages,
    // surface a "Save before closing?" prompt so the advisor doesn't lose
    // the transcript by accident. Empty / unloaded sessions close
    // straight away.
    if (messages.length > 0) {
      setCloseConfirmOpen(true);
    } else {
      setOpen(false);
    }
  }, [messages.length]);

  const confirmClose = useCallback(() => {
    setCloseConfirmOpen(false);
    setOpen(false);
  }, []);

  const cancelClose = useCallback(() => {
    setCloseConfirmOpen(false);
  }, []);

  const exportSession = useCallback(
    async (format: 'pdf' | 'csv') => {
      if (!session?.sessionId) return;
      setExportMenuOpen(false);
      // Goes through axios so the X-Brokerage-Id header gets stamped by
      // the interceptor — window.open() can't set headers and would let
      // the BFF reject the call without a tenant.
      try {
        await intelligenceApi.exportSession(session.sessionId, format);
      } catch (err) {
        console.error('Chat export failed', err);
      }
    },
    [session],
  );

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesPicked = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      // Reset the input so the same file can be re-picked after a remove.
      e.target.value = '';
      if (files.length === 0) return;

      // Browser-side mirror of the server caps so users get an immediate
      // reject for obvious violations. Server is authoritative — these
      // are convenience checks only.
      const currentTotal = attachments.reduce((sum, a) => sum + a.bytes, 0);
      const errors: string[] = [];
      const next: PendingAttachment[] = [...attachments];
      let total = currentTotal;
      for (const file of files) {
        if (next.length >= ATTACHMENT_LIMITS.maxFilesPerMessage) {
          errors.push(`Max ${ATTACHMENT_LIMITS.maxFilesPerMessage} files per message`);
          break;
        }
        if (!ATTACHMENT_LIMITS.acceptedMimeTypes.includes(file.type as never)) {
          errors.push(`${file.name}: unsupported type ${file.type || 'unknown'}`);
          continue;
        }
        if (file.size > ATTACHMENT_LIMITS.maxFileBytes) {
          errors.push(
            `${file.name}: ${humanFileSize(file.size)} exceeds per-file limit of ${humanFileSize(
              ATTACHMENT_LIMITS.maxFileBytes,
            )}`,
          );
          continue;
        }
        if (total + file.size > ATTACHMENT_LIMITS.maxTotalBytes) {
          errors.push(
            `Total upload would exceed ${humanFileSize(ATTACHMENT_LIMITS.maxTotalBytes)}`,
          );
          break;
        }
        try {
          const base64 = await readAsBase64(file);
          next.push({
            id: rid(),
            name: file.name,
            mediaType: file.type,
            bytes: file.size,
            base64,
          });
          total += file.size;
        } catch (err) {
          errors.push(`${file.name}: ${(err as Error).message ?? 'read failed'}`);
        }
      }
      setAttachments(next);
      if (errors.length > 0) setError(errors.join(' · '));
    },
    [attachments],
  );

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  /**
   * Submit a turn. `mode === 'input'` reads from the textarea (clears
   * input + attachments on send). `mode === 'programmatic'` submits a
   * caller-supplied string (used by the `pick:<fund_key>` disambiguation
   * link) without touching input/attachments — the advisor's half-typed
   * message in the textarea is preserved.
   */
  const sendTurn = useCallback(
    async (text: string, mode: 'input' | 'programmatic') => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;
      const s = await ensureSession();
      if (!s) return;

      // Attachments only ride with the live input — a programmatic pick
      // is a follow-up; attachments would have ridden with the previous
      // turn, not this one.
      const sendAttachments: ChatAttachment[] =
        mode === 'input'
          ? attachments.map((a) => ({ name: a.name, mediaType: a.mediaType, data: a.base64 }))
          : [];
      const userBubbleText =
        mode === 'input' && sendAttachments.length > 0
          ? `${trimmed}\n\n_attached: ${attachments.map((a) => a.name).join(', ')}_`
          : trimmed;

      const userMsg: UiMessage = { id: rid(), role: 'user', text: userBubbleText };
      const assistantMsg: UiMessage = { id: rid(), role: 'assistant', text: '', streaming: true };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      if (mode === 'input') {
        setInput('');
        setAttachments([]);
      }
      setSending(true);
      setError(null);
      setActiveTool(null);

      // Tools invoked + citations gathered during THIS turn — attached to the
      // assistant message on completion so the agent badges + sources persist.
      const turnTools: string[] = [];
      const turnCitations: Citation[] = [];

      abortRef.current?.();
      abortRef.current = streamMessage(s.sessionId, trimmed, {
        onToken: (delta) => {
          setMessages((prev) => updateLastAssistant(prev, (m) => ({ ...m, text: m.text + delta })));
        },
        onToolCallStarted: (call) => {
          turnTools.push(call.name);
          setActiveTool(call.name);
        },
        onToolCallCompleted: (result) => {
          if (result.citations && result.citations.length > 0) {
            turnCitations.push(...result.citations);
          }
          setActiveTool(null);
        },
        onDone: () => {
          setMessages((prev) => {
            const next = updateLastAssistant(prev, (m) => ({
              ...m,
              streaming: false,
              tools: turnTools.length > 0 ? [...turnTools] : m.tools,
              citations: turnCitations.length > 0 ? [...turnCitations] : m.citations,
            }));
            // Speak the completed assistant reply through the voice
            // hook's speak() — no-op if voice disabled / unsupported.
            // Driven off the message text so we always speak the final
            // version (including the last streamed token).
            const last = next[next.length - 1];
            if (last && last.role === 'assistant' && last.text) {
              voiceSpeakRef.current(last.text);
            }
            return next;
          });
          setSending(false);
          setActiveTool(null);
          abortRef.current = null;
        },
        onError: (msg) => {
          setError(msg);
          setMessages((prev) => updateLastAssistant(prev, (m) => ({ ...m, streaming: false })));
          setSending(false);
          setActiveTool(null);
          abortRef.current = null;
        },
      }, sendAttachments.length > 0 ? sendAttachments : undefined, pageContextRef.current);
    },
    [ensureSession, sending, attachments],
  );

  const handleSend = useCallback(() => {
    return sendTurn(input, 'input');
  }, [sendTurn, input]);

  const handlePickFund = useCallback(
    (fundKey: string) => {
      void sendTurn(`Please use ${fundKey}.`, 'programmatic');
    },
    [sendTurn],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  // Memoise the placeholder computation. Previously this ran on every
  // keystroke (the textarea's onChange triggers a parent re-render, and
  // the `[...messages].reverse().find(...)` copy + scan re-executed each
  // time). With useMemo it only updates when messages / pageContext
  // change, which is the dimension that actually influences the hint.
  const lastAssistantText = useMemo(
    () => {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') return messages[i].text;
      }
      return undefined;
    },
    [messages],
  );
  const inputPlaceholder = useMemo(
    () => companionPlaceholder({ pageContext, lastAssistantText }),
    [pageContext, lastAssistantText],
  );

  if (!open) {
    return (
      <button
        className='fund-chatbot-launcher'
        onClick={handleOpen}
        type='button'
        aria-label='Open Fund Center assistant'
        data-testid='fund-chatbot-launcher'
      >
        <img
          src='/fin-logo.svg'
          alt=''
          aria-hidden='true'
          className='fund-chatbot-launcher-logo'
        />
        Ask Fin
      </button>
    );
  }

  return (
    <aside className='fund-chatbot-dock' role='complementary' aria-label='Fund Center assistant'>
      <header className='fund-chatbot-header'>
        <div className='fund-chatbot-header-title'>
          <img
            src='/fin-logo.svg'
            alt=''
            aria-hidden='true'
            className='fund-chatbot-header-logo'
          />
          {session?.agentName ?? 'Fin'}
        </div>
        <div className='fund-chatbot-header-actions'>
          <div className='fund-chatbot-export-menu-wrapper'>
            <IconButton
              variant='ghost'
              size='sm'
              icon={<AppIcon name='more' size={18} />}
              onClick={() => setExportMenuOpen((v) => !v)}
              aria-label='Session menu'
              aria-haspopup='menu'
              aria-expanded={exportMenuOpen}
              data-testid='fund-chatbot-menu'
              disabled={!session?.sessionId}
            />
            {exportMenuOpen && (
              <div className='fund-chatbot-export-menu' role='menu' data-testid='fund-chatbot-export-menu'>
                <button
                  type='button'
                  role='menuitem'
                  onClick={() => exportSession('pdf')}
                  data-testid='fund-chatbot-export-pdf'
                >
                  Export as PDF
                </button>
                <button
                  type='button'
                  role='menuitem'
                  onClick={() => exportSession('csv')}
                  data-testid='fund-chatbot-export-csv'
                >
                  Export as CSV
                </button>
              </div>
            )}
          </div>
          <IconButton
            variant='ghost'
            size='sm'
            icon={<AppIcon name='close' size={18} />}
            onClick={handleClose}
            aria-label='Close chatbot'
            data-testid='fund-chatbot-close'
          />
        </div>
      </header>
      {closeConfirmOpen && (
        <div className='fund-chatbot-close-confirm' role='dialog' aria-label='Save before closing?' data-testid='fund-chatbot-close-confirm'>
          <div className='fund-chatbot-close-confirm-text'>
            Save this chat before closing? Once you close the dock and the
            session expires you won't be able to export the transcript.
          </div>
          <div className='fund-chatbot-close-confirm-actions'>
            <button
              type='button'
              className='fund-chatbot-close-confirm-secondary'
              onClick={() => { exportSession('pdf'); }}
              data-testid='fund-chatbot-close-confirm-export-pdf'
            >
              Export as PDF
            </button>
            <button
              type='button'
              className='fund-chatbot-close-confirm-secondary'
              onClick={() => { exportSession('csv'); }}
              data-testid='fund-chatbot-close-confirm-export-csv'
            >
              Export as CSV
            </button>
            <button
              type='button'
              className='fund-chatbot-close-confirm-cancel'
              onClick={cancelClose}
              data-testid='fund-chatbot-close-confirm-cancel'
            >
              Keep open
            </button>
            <button
              type='button'
              className='fund-chatbot-close-confirm-primary'
              onClick={confirmClose}
              data-testid='fund-chatbot-close-confirm-discard'
            >
              Close without saving
            </button>
          </div>
        </div>
      )}

      <div className='fund-chatbot-messages' ref={scrollRef} data-testid='fund-chatbot-messages'>
        {creating && messages.length === 0 && (
          <div className='fund-chatbot-tool-indicator'>Starting session…</div>
        )}
        {messages.length === 0 && !creating && (
          <div className='fund-chatbot-msg fund-chatbot-msg-assistant'>
            Hi — ask me anything about funds in your catalogue. I can search,
            look up details, compare funds, and read factsheets.
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`fund-chatbot-msg fund-chatbot-msg-${m.role}`}
            data-testid={`fund-chatbot-msg-${m.role}`}
          >
            {m.role === 'assistant' ? (
              <>
                <AssistantText
                  text={m.text}
                  sessionId={session?.sessionId}
                  onSelectFund={onSelectFund}
                  onPickFund={handlePickFund}
                />
                {agentBadges(m.tools).length > 0 && (
                  <div className='fund-chatbot-msg-agents' data-testid='fund-chatbot-msg-agents'>
                    {agentBadges(m.tools).map((agent) => (
                      <Badge key={agent.key} color={agent.color} size='sm' variant='outlined'>
                        {agent.emoji} {agent.label}
                      </Badge>
                    ))}
                  </div>
                )}
                {m.citations && m.citations.length > 0 && (
                  <div className='fund-chatbot-citations' data-testid='fund-chatbot-citations'>
                    <span className='fund-chatbot-citations-label'>Sources</span>
                    {m.citations.map((c, i) =>
                      c.url ? (
                        <a
                          key={`${c.label}-${i}`}
                          className='fund-chatbot-citation'
                          href={c.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          title={c.edition ? `${c.label} — ${c.edition}` : c.label}
                        >
                          📄 {c.label}
                        </a>
                      ) : (
                        <span
                          key={`${c.label}-${i}`}
                          className='fund-chatbot-citation fund-chatbot-citation-plain'
                          title={c.edition ? `${c.label} — ${c.edition}` : c.label}
                        >
                          {c.label}
                        </span>
                      ),
                    )}
                  </div>
                )}
              </>
            ) : (
              m.text
            )}
          </div>
        ))}
        {activeTool && (
          <div className='fund-chatbot-tool-indicator' data-testid='fund-chatbot-tool-indicator'>
            {toolIndicator(activeTool)}
          </div>
        )}
        {error && <div className='fund-chatbot-error'>{error}</div>}
      </div>

      {attachments.length > 0 && (
        <div className='fund-chatbot-attachments' data-testid='fund-chatbot-attachments'>
          {attachments.map((a) => (
            <span key={a.id} className='fund-chatbot-attachment-chip'>
              <AppIcon
                name={a.mediaType === 'application/pdf' ? 'file pdf' : 'file image'}
                size={14}
              />
              <span className='fund-chatbot-attachment-name' title={a.name}>{a.name}</span>
              <span className='fund-chatbot-attachment-size'>{humanFileSize(a.bytes)}</span>
              <button
                type='button'
                aria-label={`Remove ${a.name}`}
                onClick={() => handleRemoveAttachment(a.id)}
                className='fund-chatbot-attachment-remove'
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className='fund-chatbot-input-row'>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept={FILE_INPUT_ACCEPT}
          onChange={handleFilesPicked}
          style={{ display: 'none' }}
          data-testid='fund-chatbot-file-input'
        />
        <IconButton
          variant='ghost'
          size='md'
          onClick={handleAttachClick}
          disabled={sending || attachments.length >= ATTACHMENT_LIMITS.maxFilesPerMessage}
          icon={<AppIcon name='attach' size={18} />}
          aria-label='Attach document'
          data-testid='fund-chatbot-attach'
        />
        {session?.voiceEnabled === true && voice.recognitionSupported && (
          <IconButton
            variant='ghost'
            size='md'
            onClick={() => (voice.isRecording ? voice.stopRecording() : voice.startRecording())}
            disabled={sending}
            icon={<AppIcon name={voice.isRecording ? 'microphone slash' : 'microphone'} size={18} />}
            aria-label={voice.isRecording ? 'Stop recording' : 'Start voice input'}
            data-testid='fund-chatbot-mic'
            className={voice.isRecording ? 'fund-chatbot-mic-recording' : undefined}
          />
        )}
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={inputPlaceholder}
          disabled={sending}
          data-testid='fund-chatbot-input'
        />
        <IconButton
          variant='round-filled'
          size='md'
          onClick={() => void handleSend()}
          disabled={sending || !input.trim()}
          icon={<AppIcon name='send' size={18} color='white' />}
          aria-label='Send message'
          data-testid='fund-chatbot-send'
        />
      </div>

      <footer className='fund-chatbot-footer'>
        <span>{session ? `${session.llmProvider} · ${session.model}` : 'Connecting…'}</span>
        <span>Answers grounded in your catalogue only.</span>
      </footer>
    </aside>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

function rid(): string {
  return Math.random().toString(36).slice(2);
}

function updateLastAssistant(
  prev: UiMessage[],
  updater: (m: UiMessage) => UiMessage,
): UiMessage[] {
  if (prev.length === 0) return prev;
  const next = [...prev];
  const i = next.length - 1;
  if (next[i].role === 'assistant') {
    next[i] = updater(next[i]);
  }
  return next;
}

// Assistant text renderer. Streamed LLM output is markdown — bold,
// lists, paragraphs, code spans. We render it via react-markdown
// (+ remark-gfm for tables/strikethrough/autolinks, + remark-breaks
// so single newlines become <br>) and override the `a` component so
// `fund:<key>` and `factsheet:<key>` URIs are intercepted:
//   - fund:<key>      → onSelectFund(key) (no navigation)
//   - factsheet:<key> → opens /api/funds/{key}/factsheet in a new tab
//   - https?://       → opens in a new tab
// Heading / list / code styling is overridden so the dock's compact
// spacing isn't blown out by react-markdown defaults.
const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p style={{ margin: '0 0 10px' }}>{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol style={{ margin: '4px 0', paddingLeft: 20 }}>{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li style={{ margin: '2px 0' }}>{children}</li>
  ),
  // Verbatim source quotes (the reference agents wrap statute/code text in `>`).
  // Without this, blockquotes fall back to the UA default (~40px left+right
  // indent, no marker) which reads as mysteriously-indented prose. Style it as
  // a clear quote: left accent + light background, normal margins.
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote
      style={{
        margin: '8px 0',
        padding: '6px 12px',
        borderLeft: '3px solid #c7d2fe',
        background: '#f8fafc',
        color: '#334155',
        borderRadius: 4,
        fontStyle: 'normal',
      }}
    >
      {children}
    </blockquote>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h3 style={{ margin: '8px 0 4px', fontSize: 16 }}>{children}</h3>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h4 style={{ margin: '8px 0 4px', fontSize: 15 }}>{children}</h4>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h5 style={{ margin: '6px 0 2px', fontSize: 14 }}>{children}</h5>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    // Inline-code only — fenced code blocks come through `pre:` below.
    <code style={{ background: '#eef', padding: '0 4px', borderRadius: 3, fontSize: 13 }}>
      {children}
    </code>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => {
    // Fenced code blocks arrive as <pre><code class="language-{lang}">…</code></pre>.
    // We sniff the inner <code>'s className for `language-vega-lite` and
    // render a chart in its place — bypassing both the <pre> backplate
    // and the inline-`<code>` styling.
    const codeChild = findCodeChild(children);
    if (codeChild) {
      const className = (codeChild.props as { className?: string }).className ?? '';
      const match = /language-(\w+)/.exec(className);
      const lang = match?.[1];
      if (lang === 'vega-lite' || lang === 'vegalite' || lang === 'vega') {
        const spec = extractCodeText((codeChild.props as { children?: React.ReactNode }).children);
        return <VegaChart specJson={spec.trim()} />;
      }
      if (lang === 'mermaid') {
        const source = extractCodeText((codeChild.props as { children?: React.ReactNode }).children);
        const trimmed = source.trim();
        // R-3 mitigation: cap diagram source length. Anything bigger renders
        // as a code block so the advisor still sees the source.
        if (trimmed.length > MAX_MERMAID_SOURCE_BYTES) {
          return (
            <pre style={{ background: '#fef9c3', border: '1px solid #fde68a', padding: 8,
                          borderRadius: 6, fontSize: 12, overflow: 'auto', margin: '4px 0' }}>
              {`Diagram skipped — source exceeds ${MAX_MERMAID_SOURCE_BYTES} bytes.\n\n${trimmed.slice(0, 800)}…`}
            </pre>
          );
        }
        return <MermaidDiagram source={trimmed} />;
      }
    }
    return (
      <pre style={{ background: '#f3f4f6', padding: 8, borderRadius: 4, overflow: 'auto', fontSize: 12, margin: '4px 0' }}>
        {children}
      </pre>
    );
  },
};

function findCodeChild(children: React.ReactNode): React.ReactElement | null {
  // react-markdown 10 passes the `code` override's element (its type is the
  // override function, NOT the string 'code') as the child of <pre>. Match
  // on the presence of a `className` prop instead of element type — that's
  // the only thing we actually need to read.
  const arr = React.Children.toArray(children);
  for (const node of arr) {
    if (React.isValidElement(node)) {
      const props = node.props as { className?: string };
      if (typeof props?.className === 'string' && props.className.startsWith('language-')) {
        return node as React.ReactElement;
      }
    }
  }
  return null;
}

function extractCodeText(children: React.ReactNode): string {
  // Fenced block contents arrive as a single string child (or array of strings
  // if streaming splits the tokens). Concatenate any string children.
  const arr = React.Children.toArray(children);
  return arr
    .filter((c) => typeof c === 'string' || typeof c === 'number')
    .map((c) => String(c))
    .join('');
}

/**
 * CommonMark requires a fenced code block's opening ``` to be at the
 * start of a line (after optional leading whitespace). The LLM
 * occasionally writes the chat-confirmation fence directly after the
 * preceding sentence — e.g. "review and click Confirm.```chat-
 * confirmation\n…" — which means react-markdown treats the backticks
 * as inline code and the ConfirmationCard never renders.
 *
 * Defence-in-depth: rewrite the assistant text so any
 * ```chat-confirmation opener that isn't already on its own line gets
 * a leading blank line inserted. Exported so the unit tests can
 * exercise the heuristic directly without driving the streaming UI.
 */
export function normaliseChatConfirmationFences(text: string): string {
  if (!text) return text;
  // Match the opener when it follows a non-whitespace char on the same
  // line. We do NOT touch fences that are already at the start of a
  // line or already preceded by a blank line.
  return text.replace(/(\S)(```chat-confirmation\b)/g, '$1\n\n$2');
}

/**
 * Wrapped in React.memo so each typed keystroke in the input box
 * doesn't re-run the ReactMarkdown pipeline for every existing
 * assistant bubble. The default shallow comparison is enough since
 * `text` / `sessionId` are primitives and `onSelectFund` /
 * `onPickFund` are passed in via useCallback / props, so they're
 * stable across input keystrokes.
 */
const AssistantText = React.memo(function AssistantTextImpl({
  text,
  sessionId,
  onSelectFund,
  onPickFund,
}: {
  text: string;
  /** Active intelligence session id — required to wire the
   *  ConfirmationCard's fetch + confirm/cancel calls when a
   *  `chat-confirmation` fence appears in the assistant reply. */
  sessionId?: string;
  onSelectFund?: (fundKey: string) => void;
  /** Click handler for `pick:<fund_key>` disambiguation links. */
  onPickFund?: (fundKey: string) => void;
}) {
  if (!text) {
    return <span aria-hidden='true'>…</span>;
  }
  // Bind the link component to the current onSelectFund handler — react-markdown
  // expects a stable component for `a`, and we want closure over the prop.
  const defaultPre = markdownComponents.pre;
  const components = {
    ...markdownComponents,
    pre: ({ children }: { children?: React.ReactNode }) => {
      // Phase 4 (Advisor Companion): when Fin emits a
      // `chat-confirmation` fenced block after a propose_* tool call,
      // replace it with the inline <ConfirmationCard/>. Falls through
      // to the default pre for vega/mermaid/regular code blocks.
      const codeChild = findCodeChild(children);
      if (codeChild) {
        const className = (codeChild.props as { className?: string }).className ?? '';
        const match = /language-(\w[\w-]*)/.exec(className);
        const lang = match?.[1];
        if (lang === 'chat-confirmation') {
          const raw = extractCodeText((codeChild.props as { children?: React.ReactNode }).children);
          try {
            const parsed = JSON.parse(raw) as { proposalId?: string };
            if (parsed.proposalId && sessionId) {
              return <ConfirmationCard sessionId={sessionId} proposalId={parsed.proposalId} />;
            }
          } catch {
            // Fall through to default rendering — advisor still sees
            // the source block so they can flag the malformed fence.
          }
        }
      }
      return defaultPre({ children });
    },
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      if (href && href.startsWith('pick:')) {
        const fundKey = href.slice('pick:'.length);
        return (
          <a
            href='#'
            className='fund-chatbot-pick-link'
            data-testid={`fund-chatbot-pick-${fundKey}`}
            onClick={(e) => {
              e.preventDefault();
              onPickFund?.(fundKey);
            }}
          >
            {children}
          </a>
        );
      }
      if (href && href.startsWith('fund:')) {
        const fundKey = href.slice('fund:'.length);
        return (
          <a
            href='#'
            onClick={(e) => {
              e.preventDefault();
              onSelectFund?.(fundKey);
            }}
          >
            {children}
          </a>
        );
      }
      if (href && href.startsWith('factsheet:')) {
        const fundKey = href.slice('factsheet:'.length);
        const url = `/api/funds/${encodeURIComponent(fundKey)}/factsheet`;
        return (
          <a href={url} target='_blank' rel='noopener noreferrer'>
            {children}
          </a>
        );
      }
      // cashflow-export:{profileId}[:{scenarioId|base}] — first-party CSV
      // download via the axios client (carries cookie + X-Brokerage-Id); no
      // bytes flow through the LLM (PRD §7 / D-3).
      if (href && href.startsWith('cashflow-export:')) {
        const rest = href.slice('cashflow-export:'.length);
        const [exportProfileId, exportScenarioId] = rest.split(':');
        return (
          <a
            href='#'
            className='fund-chatbot-cashflow-export-link'
            data-testid='fund-chatbot-cashflow-export'
            onClick={(e) => {
              e.preventDefault();
              if (exportProfileId) {
                void relationshipCashflowApi.downloadCashflowCsv(
                  exportProfileId,
                  exportScenarioId ?? null,
                );
              }
            }}
          >
            {children}
          </a>
        );
      }
      return (
        <a href={href} target='_blank' rel='noopener noreferrer'>
          {children}
        </a>
      );
    },
  };
  // react-markdown's default urlTransform strips URIs whose scheme isn't
  // http/https/mailto/tel — that would silently drop our custom
  // `fund:<key>`, `factsheet:<key>` and `pick:<key>` links. Allow them
  // explicitly; everything else still goes through a safe-scheme filter
  // so a prompt-injected `javascript:` URL can't be rendered.
  const safeUrlTransform = (url: string): string => {
    if (
      url.startsWith('fund:') ||
      url.startsWith('factsheet:') ||
      url.startsWith('pick:') ||
      url.startsWith('cashflow-export:')
    ) {
      return url;
    }
    if (/^(https?|mailto|tel):/i.test(url)) return url;
    // Relative paths, anchors, scheme-less identifiers — preserve.
    if (url.startsWith('#') || url.startsWith('/') || !url.includes(':')) return url;
    return '';
  };
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={components}
      urlTransform={safeUrlTransform}
      // Disable raw HTML so prompt-injection markup can't sneak through.
      skipHtml
    >
      {normaliseChatConfirmationFences(text)}
    </ReactMarkdown>
  );
});
